<i18n>
{
  "fr": {
    "note-label": "Note de suivi",
    "show-more": "Voir plus",
    "show-less": "Voir moins"
  },
  "en": {
    "note-label": "Notes",
    "show-more": "Show more",
    "show-less": "Show less"
  }
}
</i18n>
<template>
  <component
    :is="wrapperComponent"
    v-bind="wrapperProps"
    class="indicator-snippet has-text-black-ter"
    :class="{
      'indicator-snippet--closed': snippetExceedsHeight && !snippetIsOpened,
      'indicator-snippet--enable-transitions': enableTransitions
    }"
    :style="{ 'max-height': maxDrawerHeight }"
  >
    <div
      class="indicator-snippet__filter"
      :class="{ 'indicator-snippet__filter--active': !snippetIsOpened && snippetExceedsHeight }"
    ></div>
    <div
      class="indicator-snippet__box"
      :class="{
        'indicator-snippet__box--closed': snippetExceedsHeight && !snippetIsOpened,
        'indicator-snippet__box--enable-transitions': enableTransitions
      }"
    >
      <div
        ref="snippetBody"
        class="indicator-snippet__inside"
        :class="{
          'is-block': condensed,
          'is-flex': !condensed
        }"
      >
        <div style="flex: 1 1 0" :style="{ 'max-height': maxDrawerHeight }" class="indicator-snippet__left-col p-12px">
          <div ref="leftCol" class="is-flex" style="justify-content: space-between">
            <div>
              <div v-if="projectName" class="mb-4px ml-24px is-size-7 has-text-weight-bold has-text-grey">{{ projectName }}</div>
              <div class="is-flex">
                <div>
                  <difficulty-icon class="indicator-snippet__icon has-text-grey" :indicator="data" isRight />
                </div>
                <div>
                  <router-link :to="indicatorRoute" class="mb-4px has-text-link has-text-weight-medium">{{
                    data.title
                  }}</router-link>
                  <div v-if="ownerName" class="is-size-7 has-text-weight-bold">{{ ownerName }}</div>
                  <div v-if="showDate && data.targetDate" class="is-size-7">{{ data.targetDate }}</div>
                  <div v-if="showNote && data.notes && data.notes.trim()" class="mt-3 is-size-7 has-text-grey">
                    <span class="is-block has-text-weight-bold mb-1">{{ $t('note-label') }}</span>
                    <span class="has-white-space-pre-line has-text-black-ter is-block" :class="{ 'pb-24px': !showDocuments }">{{
                      data.notes
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="ml-8px" v-if="condensed || !showDocuments">
              <status-badge :indicator="data" />
            </div>
            <a
              v-if="snippetExceedsHeight && !showDocuments"
              class="indicator-snippet__toggle-more is-size-7 py-8px mr-12px"
              :class="{ 'indicator-snippet__toggle-more--active': snippetIsOpened }"
              @click.prevent="snippetIsOpened = !snippetIsOpened"
            >
              <span v-if="snippetIsOpened">{{ $t('show-less') }} <b-icon icon="chevron-up" /></span>
              <span v-else>{{ $t('show-more') }} <b-icon icon="chevron-down" /></span>
            </a>
          </div>
        </div>

        <div v-if="showDocuments" ref="rightCol" class="indicator-snippet__documents p-12px" :class="{ 'ml-3': !condensed }">
          <div class="is-flex" style="justify-content: space-between">
            <div class="mb-4">
              <span
                v-if="data.stats && data.stats.documentsCount > 0"
                class="indicator-snippet__documents-count is-block is-size-7 has-text-grey-darker has-text-weight-medium"
              >
                {{ data.stats.completedDocumentsCount }}/{{ data.stats.documentsCount }}
              </span>
            </div>
            <template v-if="!condensed">
              <status-badge class="mx-12px" :indicator="data" show-label />
            </template>
          </div>
          <div
            v-if="showDocuments"
            class="indicator-snippet__inside-documents"
            :class="{ 'indicator-snippet__inside-documents--exceedsHeight': snippetExceedsHeight }"
          >
            <router-link
              class="document mb-1 px-3 py-1"
              :class="{ 'document--done': doc.isDone }"
              :to="{ ...indicatorRoute, hash: `#doc-${doc.id}` }"
              v-for="doc in documents.slice(0, 2)"
              :key="doc.id"
            >
              <p class="document__title">{{ doc.title }}</p>
              <div @click.prevent>
                <ui-tooltip
                  v-if="doc.notes"
                  :label="$t('note-label')"
                  :content="$format.nl2br(doc.notes)"
                  mobilePosition="is-left"
                ></ui-tooltip>
              </div>
            </router-link>
            <div v-if="documents.length > 2" class="indicator-snippet__more-documents" :style="{ 'max-height': maxDrawerHeight }">
              <router-link
                class="document mb-1 px-3 py-1"
                :class="{ 'document--done': doc.isDone }"
                :to="{ ...indicatorRoute, hash: `#doc-${doc.id}` }"
                v-for="doc in documents.slice(2, documents.length)"
                :key="doc.id"
              >
                <p class="document__title">{{ doc.title }}</p>
                <div @click.prevent>
                  <ui-tooltip
                    v-if="doc.notes"
                    :label="$t('note-label')"
                    :content="$format.nl2br(doc.notes)"
                    mobilePosition="is-left"
                  ></ui-tooltip>
                </div>
              </router-link>
            </div>
          </div>

          <a
            v-if="snippetExceedsHeight"
            class="indicator-snippet__toggle-more is-size-7 py-8px mr-12px"
            :class="{ 'indicator-snippet__toggle-more--active': snippetIsOpened }"
            @click.prevent="snippetIsOpened = !snippetIsOpened"
          >
            <span v-if="snippetIsOpened">{{ $t('show-less') }} <b-icon icon="chevron-up" /></span>
            <span v-else>{{ $t('show-more') }} <b-icon icon="chevron-down" /></span>
          </a>
        </div>
      </div>
    </div>
  </component>
</template>
<script>
import DifficultyIcon from '@/components/indicator/difficulty-icon'
import StatusBadge from '@/components/indicator/status-badge'
import UiTooltip from '@/components/ui/tooltip'
import { URL_INDICATOR_PROJECT, URL_INDICATOR_GENERAL } from '@/consts/urls'

export default {
  components: { DifficultyIcon, StatusBadge, UiTooltip },
  props: {
    data: {
      type: Object,
      required: true
    },
    clickable: Boolean,
    condensed: Boolean,
    showNote: Boolean,
    showDocuments: Boolean,
    showDate: Boolean
  },
  data() {
    return {
      snippetIsOpened: false,
      snippetExceedsHeight: false,
      enableTransitions: false,
      drawerHeight: null
    }
  },
  computed: {
    wrapperComponent() {
      return this.clickable ? 'router-link' : 'div'
    },
    wrapperProps() {
      if (this.clickable) {
        return {
          to: this.indicatorRoute
        }
      }

      return null
    },
    ownerName() {
      if (!this.data.owner || !this.data.owner.profile) return ''
      return `${this.data.owner.profile.firstName} ${this.data.owner.profile.lastName}`
    },
    projectName() {
      if (!this.data.project) return ''
      return this.data.project.name
    },
    indicatorRoute() {
      if (this.data.project) {
        return { name: URL_INDICATOR_PROJECT, params: { projectId: this.data.project.id, id: this.data.id } }
      } else {
        return { name: URL_INDICATOR_GENERAL, params: { id: this.data.id } }
      }
    },
    documents() {
      const sortedDocuments = [...this.data.documents].sort((a, b) => {
        if (a.isDone !== b.isDone) return b.isDone ? -1 : 1
        return a.sortOrder - b.sortOrder
      })

      return sortedDocuments
    },
    maxDrawerHeight() {
      if (!this.snippetIsOpened) return ''
      return `${this.drawerHeight}px`
    }
  },
  methods: {
    setSnippetMaxHeight() {
      const media = window.matchMedia('(min-width: 768px)')
      const leftCol = this.$refs.leftCol
      const rightCol = this.$refs.rightCol
      let leftColHeight = 0
      let rightColHeight = 0

      // Left col height + y padding
      if (leftCol) leftColHeight = leftCol.offsetHeight + 12 * 2
      // Right col height + height of firsts fixed items in col and y padding
      if (rightCol) rightColHeight = rightCol.offsetHeight + 114 + 12 * 2
      // According to media query
      if (media.matches) {
        // Pick heighest element between the two to set drawer height
        if (leftColHeight > rightColHeight) this.drawerHeight = leftColHeight
        else this.drawerHeight = rightColHeight
      }
      // Or add both leftCol and rightCol heights
      else this.drawerHeight = leftColHeight + rightColHeight
    },
    checkSnippetHeight() {
      this.enableTransitions = false
      setTimeout(() => (this.enableTransitions = true), 100)

      if (this.showNote || this.showDocuments) {
        const snippetBody = this.$refs.snippetBody
        const height = snippetBody.offsetHeight

        this.snippetExceedsHeight = height > 200
        this.setSnippetMaxHeight()
      } else {
        this.snippetExceedsHeight = false
      }
    }
  },
  mounted() {
    this.checkSnippetHeight()
    window.addEventListener('resize', this.checkSnippetHeight)
  },
  destroyed() {
    window.removeEventListener('resize', this.checkSnippetHeight)
  }
}
</script>
<fragments>
fragment indicatorSnippet on IndicatorGraphType {
  id
  language
  title
  owner {
    id
    profile {
      id
      firstName
      lastName
    }
  }
  stats {
    documentsCount
    completedDocumentsCount
  }
  targetDate
  status
  difficulty
  notes
  ... on ProjectIndicatorGraphType {
    project {
      id
      name
    }
  }
}

fragment indicatorSnippetDocuments on IndicatorGraphType {
  id
  documents {
    id
    title
    notes
    isDone
    sortOrder
  }
}
</fragments>

<style lang="scss" scoped>
.indicator-snippet {
  border: 1px solid $grey-lighter;
  position: relative;
  overflow: visible;

  &--closed {
    max-height: 200px;
    overflow: hidden;
    @include tablet {
      overflow: visible;
    }

    .indicator-snippet__left-col {
      max-height: 200px - 2px; // Fix overflow on border-bottom
    }

    .indicator-snippet__more-documents {
      max-height: 70px;
      overflow: hidden;
      pointer-events: none;
    }
  }

  &--enable-transitions {
    transition: max-height 0.5s ease-in-out;

    &.indicator-snippet--closed {
      transition: max-height 0.2s ease-in-out;

      .indicator-snippet__filter {
        transition: opacity 0.1s ease-in-out;
      }

      .indicator-snippet__left-col {
        transition: max-height 0.2s ease-in-out;
      }

      .indicator-snippet__more-documents {
        transition: max-height 0.2s ease-in-out;
      }
    }

    &.indicator-snippet__box--closed {
      transition: max-height 0.2s ease-in-out;
    }

    .indicator-snippet__filter {
      transition: opacity 0.3s ease-in-out;
    }

    .indicator-snippet__left-col {
      transition: max-height 0.5s ease-in-out;
    }

    .indicator-snippet__more-documents {
      transition: max-height 0.5s ease-in-out;
    }
  }

  &:not(:last-child) {
    margin-bottom: 8px;
  }

  &__left-col {
    overflow: hidden;
  }

  &__more-documents {
    overflow: visible;
  }

  &__toggle-more {
    animation: toggle-more 0.5s ease;

    &--active {
      animation: toggle-less 0.5s ease;
    }
  }

  &__box {
    max-height: 100%;
    // overflow: hidden;
    position: relative;

    &--closed {
      max-height: calc(200px - 2px); // Fix follow-up note bleed with bottom-border
    }

    &--enable-transitions {
      transition: max-height 0.5s ease-in-out;
    }
  }

  &__filter {
    position: absolute;
    pointer-events: none;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    background: rgb(255, 255, 255);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 76px, rgba(255, 255, 255, 1) 100%);
    opacity: 0;
    z-index: 2;

    &--active {
      opacity: 1;
    }
  }

  &__inside {
    position: relative;
  }

  &__documents {
    flex: 1 1 0;
    min-width: 0;
  }

  &__documents-count {
    line-height: 1;
  }

  &__inside-documents {
    &--exceedsHeight {
      padding-bottom: 30px;
    }
  }

  &__toggle-more {
    position: absolute;
    z-index: 2;
    top: calc(200px - 40px); // substract inside padding + element height
    right: -6px;

    @include tablet {
      right: 7px;
    }

    &--active {
      top: calc(100% - 40px);
    }
  }
}

@keyframes toggle-less {
  0% {
    opacity: 0;
  }

  75% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

@keyframes toggle-more {
  0% {
    opacity: 0;
  }

  75% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

@include tablet {
  &__filter {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0) calc(200px - 76px),
      rgba(255, 255, 255, 1) 100%
    );
  }
}

.document {
  align-items: center;
  border: 1px solid $grey-lighter;
  border-radius: 4px;
  color: $grey-darker;
  display: flex;
  justify-content: space-between;

  &--done {
    .document__title {
      opacity: 0.5;
    }
  }

  &__title {
    font-size: 0.75rem;
    font-weight: bold;
    line-height: 25px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
